import { useRef, useState } from "react";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import {
  WhatsappLogo,
  XLogo,
  InstagramLogo,
  Copy,
  DownloadSimple,
  Quotes,
} from "@phosphor-icons/react";
import { topicStyle } from "@/lib/theme";
import ShareableQuote from "@/components/ShareableQuote";

const shareText = (e) =>
  `"${e.quote}"\n\n— ${e.author}, ${e.book}\n\nvia The Daily Forge`;

export const ExtractCard = ({ extract, index }) => {
  const ts = topicStyle(extract.topic);
  const captureRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareText(extract))}`,
      "_blank"
    );
  };

  const handleX = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText(extract))}`,
      "_blank"
    );
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareText(extract));
    toast.success("Extract copied to clipboard");
  };

  const renderCanvas = async () =>
    html2canvas(captureRef.current, {
      backgroundColor: "#0c0a09",
      scale: 1,
      useCORS: true,
      allowTaint: true,
    });

  const canvasToFile = (canvas) =>
    new Promise((resolve) =>
      canvas.toBlob(
        (blob) => resolve(new File([blob], `daily-forge-${extract.id}.png`, { type: "image/png" })),
        "image/png"
      )
    );

  const downloadCanvas = (canvas) => {
    const link = document.createElement("a");
    link.download = `daily-forge-${extract.id}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const handleDownload = async () => {
    if (!captureRef.current || busy) return;
    setBusy(true);
    try {
      downloadCanvas(await renderCanvas());
      toast.success("Quote image saved — share it anywhere");
    } catch (err) {
      toast.error("Could not generate image");
    } finally {
      setBusy(false);
    }
  };

  // Native share sheet with the branded image file (surfaces Instagram / Stories on mobile).
  const handleShareImage = async () => {
    if (!captureRef.current || busy) return;
    setBusy(true);
    try {
      const canvas = await renderCanvas();
      const file = await canvasToFile(canvas);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "The Daily Forge",
          text: shareText(extract),
        });
      } else {
        downloadCanvas(canvas);
        toast.success("Image saved — open Instagram and post it to your feed or story");
      }
    } catch (err) {
      if (err && err.name !== "AbortError") {
        toast.error("Could not share image");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.article
      data-testid={`extract-card-${extract.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className="relative bg-stone-900/40 border border-stone-800 rounded-sm p-7 sm:p-10"
    >
      <ShareableQuote ref={captureRef} extract={extract} />

      <div className="flex items-start justify-between gap-4">
        <span
          data-testid={`extract-topic-${extract.id}`}
          className="inline-block font-mono-ui uppercase text-[10px] sm:text-xs px-3 py-1 rounded-sm border"
          style={{ color: ts.color, borderColor: `${ts.color}80`, letterSpacing: "0.2em" }}
        >
          {ts.label}
        </span>
        <span className="font-mono-ui text-stone-700 text-xs tabular-nums">
          {String(index + 1).padStart(2, "0")} / 05
        </span>
      </div>

      <Quotes size={32} weight="fill" className="mt-7 mb-3" style={{ color: `${ts.color}` }} />

      <p className="font-serif-display text-2xl sm:text-3xl text-stone-100 leading-snug">
        {extract.quote}
      </p>

      <div className="mt-8 flex flex-col gap-0.5">
        <span className="text-stone-100 font-semibold text-sm sm:text-base">
          {extract.author}
        </span>
        <span className="text-stone-500 italic text-sm">{extract.book}</span>
      </div>

      <div className="mt-6 pt-4 border-t border-stone-800/60 flex items-center justify-end gap-5">
        <button
          data-testid={`share-whatsapp-${extract.id}`}
          onClick={handleWhatsApp}
          title="Share on WhatsApp"
          className="text-stone-500 hover:text-green-500 transition-colors duration-150"
        >
          <WhatsappLogo size={22} weight="fill" />
        </button>
        <button
          data-testid={`share-x-${extract.id}`}
          onClick={handleX}
          title="Share on X"
          className="text-stone-500 hover:text-stone-50 transition-colors duration-150"
        >
          <XLogo size={20} weight="fill" />
        </button>
        <button
          data-testid={`copy-extract-${extract.id}`}
          onClick={handleCopy}
          title="Copy text"
          className="text-stone-500 hover:text-stone-50 transition-colors duration-150"
        >
          <Copy size={21} />
        </button>
        <button
          data-testid={`share-instagram-${extract.id}`}
          onClick={handleShareImage}
          disabled={busy}
          title="Share to Instagram / Stories"
          className="text-stone-500 hover:text-pink-500 transition-colors duration-150 disabled:opacity-40"
        >
          <InstagramLogo size={22} weight="fill" />
        </button>
        <button
          data-testid={`download-image-${extract.id}`}
          onClick={handleDownload}
          disabled={busy}
          title="Save branded image"
          className="flex items-center gap-2 text-stone-500 hover:text-orange-500 transition-colors duration-150 disabled:opacity-40"
        >
          <DownloadSimple size={21} weight={busy ? "regular" : "bold"} />
        </button>
      </div>
    </motion.article>
  );
};

export default ExtractCard;
